<template>
  <a-modal
    :visible="visible"
    :title="`Update Roles for ${userName}`"
    @ok="handleOk"
    @cancel="handleCancel"
    @update:visible="val => $emit('update:visible', val)"
    ok-text="Update Roles"
    cancel-text="Cancel"
    :confirm-loading="loading"
  >
    <a-form layout="vertical">
      <a-form-item label="Select Roles">
        <a-select
          v-model:value="selectedRoles"
          mode="multiple"
          style="width: 100%"
          placeholder="Select roles"
          :options="roleOptions"
          :field-names="{ label: 'name', value: 'id' }"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import assessmentApi from '@/api/assessment';
import { getItem } from '@/utility/localStorageControl';

export default {
  name: 'RoleUpdateModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: [String, Number],
      default: null,
    },
    userName: {
      type: String,
      default: '',
    },
    userRoles: {
      type: [String, Array],
      default: () => [],
    },
  },
  emits: ['update:visible', 'update:roles'],
  setup(props, { emit }) {
    const loading = ref(false);
    const selectedRoles = ref([]);
    const roleOptions = ref([]);

    // Fetch roles from API
    const fetchRoles = async () => {
      try {
        loading.value = true;
        const accessToken = getItem('access_token');
        const headers = {};
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        const response = await assessmentApi.get('roles', { headers });
        if (response.data && Array.isArray(response.data)) {
          roleOptions.value = response.data;
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        message.error('Failed to load roles');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchRoles();
    });

    // No need to watch for store changes since we're directly setting roleOptions from API

    // Map roleOptions to the format expected by a-select
    const mappedRoleOptions = computed(() => {
      return roleOptions.value.map(role => ({
        value: role.id,
        label: role.name,
      }));
    });

    // Update selected roles when userRoles prop changes
    watch(
      () => props.userRoles,
      newRoles => {
        if (Array.isArray(newRoles)) {
          selectedRoles.value = newRoles.map(role => (typeof role === 'object' ? role.id : role));
        } else if (typeof newRoles === 'string') {
          selectedRoles.value = newRoles.split(',').map(r => r.trim());
        } else {
          selectedRoles.value = [];
        }
      },
      { immediate: true },
    );

    const handleOk = async () => {
      try {
        loading.value = true;

        // Get the selected role names
        const rolesToUpdate = roleOptions.value
          .filter(role => selectedRoles.value.includes(role.id))
          .map(role => role.id);

        // Emit the update event with the new roles
        emit('update:roles', {
          userId: props.userId,
          name: props.userName,
          roles: rolesToUpdate,
        });

        // Close the modal
        emit('update:visible', false);
        message.success('Roles updated successfully');
      } catch (error) {
        console.error('Error updating roles:', error);
        message.error('Failed to update roles');
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      emit('update:visible', false);
    };

    return {
      loading,
      selectedRoles,
      roleOptions: mappedRoleOptions,
      handleOk,
      handleCancel,
    };
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>
